/**
 * Small generic helpers shared across the engine, game and UI layers.
 *
 * Keeping these in one module avoids the near-identical private copies that
 * otherwise drift apart between files.
 */

/** Clamp `v` into the inclusive range `[lo, hi]`. */
export function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

/** Convert a `#rrggbb` color plus an alpha (0..1) into an `rgba()` string. */
export function withAlpha(hex: string, a: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${a})`;
}

/** Trace a rounded-rectangle subpath on `ctx` (does not fill or stroke). */
export function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const k = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + k, y);
  ctx.arcTo(x + w, y, x + w, y + h, k);
  ctx.arcTo(x + w, y + h, x, y + h, k);
  ctx.arcTo(x, y + h, x, y, k);
  ctx.arcTo(x, y, x + w, y, k);
  ctx.closePath();
}

/**
 * mulberry32 bit-mixing finalizer: maps a 32-bit integer to a float in [0, 1).
 * Shared by the seeded {@link Rng} and by deterministic visual jitter in the
 * renderer.
 */
export function mulberry32(s: number): number {
  let t = s | 0;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
}
